import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material'
import { Email, Sms, ArrowBack } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { getSaveFriendlyError } from '@utils/errorHandling'

type ResetMethod = 'email' | 'sms'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [method, setMethod] = useState<ResetMethod>('email')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleMethodChange = (_: React.MouseEvent<HTMLElement>, newMethod: ResetMethod | null) => {
    if (newMethod) {
      setMethod(newMethod)
      setError(null)
      setSuccess(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    // Validation
    if (method === 'email' && !email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (method === 'sms' && !phoneNumber.trim()) {
      setError('Please enter your phone number')
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.forgotPassword({
        email: method === 'email' ? email : undefined,
        phoneNumber: method === 'sms' ? phoneNumber : undefined,
        method,
      })

      setSuccess(response.message)

      // Navigate based on method
      if (method === 'email') {
        // For email, user receives token via email, direct them to reset page
        setTimeout(() => {
          navigate('/reset-password', { state: { email, method: 'email' } })
        }, 2000)
      } else {
        // For SMS, navigate to verification page
        setTimeout(() => {
          navigate('/verify-reset-code', { state: { phoneNumber } })
        }, 1500)
      }
    } catch (err: any) {
      const friendlyError = getSaveFriendlyError(err)
      setError(friendlyError.message || 'Failed to request password reset. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          Forgot Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Choose how you'd like to reset your password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Method Selection */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={method}
              exclusive
              onChange={handleMethodChange}
              aria-label="reset method"
              fullWidth
              disabled={isLoading}
            >
              <ToggleButton value="email" aria-label="email">
                <Email sx={{ mr: 1 }} />
                Email
              </ToggleButton>
              <ToggleButton value="sms" aria-label="sms">
                <Sms sx={{ mr: 1 }} />
                SMS
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Email Input */}
          {method === 'email' && (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoFocus
              sx={{ mb: 3 }}
              placeholder="Enter your email address"
            />
          )}

          {/* Phone Number Input */}
          {method === 'sms' && (
            <TextField
              fullWidth
              label="Phone Number"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isLoading}
              autoFocus
              sx={{ mb: 3 }}
              placeholder="+1234567890"
              helperText="Include country code (e.g., +1 for US)"
            />
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              `Send Reset ${method === 'email' ? 'Email' : 'Code'}`
            )}
          </Button>

          {/* Back to Login */}
          <Button
            fullWidth
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/login')}
            disabled={isLoading}
          >
            Back to Login
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}
